import { ErrorModal, MesfModal, useUserContext } from "@dexteel/mesf-core";
import { Grid2, TextField } from "@mui/material";
import { useEffect, useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";

import { FooterModalButtons } from "@/controls/buttons/FooterModalButtons";
import { CenteredLazyLoading } from "@/controls/LazyLoading";
import { NotificationSnackBar } from "@/controls/snackbars/notification-snackbar";

import { HelmetDexteel } from "@dexteel/mesf-core";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
	CATALOG_SECTION_FAMILY_INITIAL_VALUES,
	CatalogSectionFamily,
} from "../../../models/CatalogSectionFamily";
import {
	deleteSectionFamily,
	upsertSectionFamily,
} from "../../../repositories/CatalogSectionFamilyRepository";
import { useSectionFamilies } from "../CatalogSectionFamilyPage";
import ColorPicker from "@/pages/configuration/delay-categories/components/ColorPicker/ColorPicker";

type Props = {
	sectionFamilyId: number | null;
	modal: "create" | "update" | "delete" | "";
	onHide: (shouldUpdate: boolean) => void;
};
export const UpsertDeleteModal = ({
	sectionFamilyId,
	modal,
	onHide,
}: Props) => {
	const {
		state: { defaultAreaId },
	} = useUserContext();

	const [title, setTitle] = useState<string>("CREATE");
	const [message, setNotificationMessage] = useState<string>("");
	const [error, setError] = useState<string>("");
	const [isSubmitLoading, setIsSubmitLoading] = useState<boolean>(false);

	const succesfullyMessage = `The section family was ${modal}d succesfully`;

	const { data, isLoading } = useSectionFamilies(
		sectionFamilyId ?? undefined,
		(error) => setError(error.message),
	);
	const sectionFamily = Array.isArray(data) ? (data[0] ?? null) : data;

	const { control, setValue, reset, handleSubmit } =
		useForm<CatalogSectionFamily>({
			defaultValues: CATALOG_SECTION_FAMILY_INITIAL_VALUES,
		});

	const queryClient = useQueryClient();
	const deleteMutation = useMutation(deleteSectionFamily, {
		onSuccess: async () => {
			await queryClient.invalidateQueries(["sectionFamilies"]);
			setNotificationMessage(succesfullyMessage);
			onHide(true);
		},
		onError: (error: Error) => {
			setError(error.message);
		},
		onSettled: () => setIsSubmitLoading(false),
	});
	const upsertMutation = useMutation(upsertSectionFamily, {
		onSuccess: async () => {
			await queryClient.invalidateQueries(["sectionFamilies"]);
			setNotificationMessage(succesfullyMessage);
			onHide(true);
		},
		onError: (error: Error) => {
			setError(error.message);
		},
		onSettled: () => setIsSubmitLoading(false),
	});

	const onSubmit: SubmitHandler<CatalogSectionFamily> = async (data) => {
		setIsSubmitLoading(true);
		if (modal === "delete") {
			deleteMutation.mutate({
				sectionFamilyId: data.SectionFamilyId,
				defaultAreaId,
			});
		} else {
			upsertMutation.mutate({ sectionFamily: data, defaultAreaId });
		}
	};

	useEffect(() => {
		if (modal !== "") {
			setIsSubmitLoading(false);
			reset();
			setTitle(
				modal === "create" ? "CREATE" : modal === "update" ? "EDIT" : "DELETE",
			);
			if (modal !== "create")
				if (sectionFamily && !isLoading) {
					setValue("SectionFamilyId", sectionFamily?.SectionFamilyId);
					setValue("SectionFamilyName", sectionFamily?.SectionFamilyName);
					setValue("SectionFamilyColor", sectionFamily?.SectionFamilyColor);
				}
		}
	}, [modal, isLoading]);

	return (
		<>
			{(modal === "create" || modal === "update" || modal === "delete") && (
				<HelmetDexteel
					title={`${
						modal === "create"
							? "New"
							: `${modal === "update" ? "Edit" : "Delete"}`
					} Section Family - Section Families`}
				/>
			)}
			<MesfModal
				title={`${title} SECTION FAMILY`}
				open={modal !== ""}
				handleClose={() => onHide(false)}
				maxWidth="sm"
				id="section-family-modal"
			>
				<form onSubmit={handleSubmit(onSubmit)}>
					<MesfModal.Content dividers style={{ padding: "10px 30px" }}>
						{isLoading && <CenteredLazyLoading />}
						{!isLoading && (
							<>
								<Grid2 container spacing={1} alignItems="center">
									<Grid2 size={{ xs: 9 }}>
										<Controller
											name="SectionFamilyName"
											control={control}
											rules={{ required: "Name is required" }}
											render={({
												field: { onChange, value },
												fieldState: { error },
											}) => (
												<TextField
													required
													label="Name"
													variant="outlined"
													fullWidth
													margin="dense"
													autoComplete="off"
													value={value}
													disabled={modal === "delete"}
													onChange={onChange}
													error={!!error}
													helperText={error?.message}
												/>
											)}
										/>
									</Grid2>
									<Grid2 size={{ xs: 3, md: 3 }} style={{ marginTop: 10 }}>
										<Controller
											name="SectionFamilyColor"
											control={control}
											rules={{ required: "Color is required" }}
											render={({ field, fieldState: { error } }) => (
												<>
													<ColorPicker
														onChange={(_: string, color: string) =>
															field.onChange(color)
														}
														color={field.value}
														disabled={modal === "delete"}
														label="Color"
														colorMode="light"
													/>
													{error && (
														<span
															style={{
																fontSize: 12,
																color: "#F44336",
															}}
														>
															{error?.message}
														</span>
													)}
												</>
											)}
										/>
									</Grid2>
								</Grid2>
							</>
						)}
					</MesfModal.Content>
					<MesfModal.Actions style={{ padding: "20px 30px 30px" }}>
						<FooterModalButtons
							isSubmitLoading={isSubmitLoading}
							onHide={onHide}
							buttonLabel={modal === "delete" ? "Delete" : "Save"}
							buttonColor={modal === "delete" ? "secondary" : "primary"}
						/>
					</MesfModal.Actions>
				</form>
			</MesfModal>
			<NotificationSnackBar
				message={message}
				onHide={() => setNotificationMessage("")}
			/>
			<ErrorModal
				error={error}
				onHide={() => setError("")}
				title="Error In Section Family"
			/>
		</>
	);
};
